import { Reference } from '@approvalmax/types';
import { arrayHelpers } from '@approvalmax/utils';
import { useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';
import { QueryKeys, selectors, statics } from 'modules/common';
import { domain, du } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { changeQbooksJournalEntryRequest } from 'pages/requestForm/actions';
import { ExchangeRateChangeParams } from 'pages/requestForm/components/ExchangeRateWithUpdate/ExchangeRateWithUpdate.types';
import { getQBooksContext } from 'pages/requestForm/selectors/qbooks/qbooksSelectors';
import { getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { api } from 'services/api';

import { getRuleTypeByEditMode } from '../../../utils/helpers';
import {
    ExpandedJournalEntryLine,
    JournalEntryFields,
    QBooksJournalEntryLineWithTaxCode,
    TaxCodeListItem,
} from './QBooksJournalEntryRequestForm.types';
import {
    checkJournalEntryLineIsEmpty,
    createNewJournalEntryLine,
    expandJournalEntryLine,
    findActiveEffectiveRate,
    getUnavailableTaxRateIds,
} from './QBooksJournalEntryRequestForm.utils';

export const useFields = (props: { companyCurrency: string }) => {
    const { companyCurrency } = props;

    const dispatch = useDispatch();

    const [fieldValues, setFieldValues] = useState<JournalEntryFields>({
        currency: statics.currency.findCurrencyByCode(companyCurrency) || {
            id: companyCurrency,
            text: companyCurrency,
        },
        journalDate: '',
        journalNumber: '',
        memo: '',
        lineAmountType: domain.LineAmountType.TaxExclusive,
        exchangeRate: null,
        isManualExchangeRate: false,
        autoExchangeRate: null,
    });

    const onFieldChange = useCallback(
        <K extends keyof JournalEntryFields>(key: K, value: JournalEntryFields[K]) => {
            setFieldValues((current) => ({
                ...current,
                [key]: value,
            }));

            dispatch(changeQbooksJournalEntryRequest());
        },
        [dispatch]
    );

    const setFieldsValuesBulk = useCallback((values: Partial<JournalEntryFields>) => {
        setFieldValues((current) => ({
            ...current,
            ...values,
        }));
    }, []);

    const onCurrencyChange = useCallback(
        (newValue: Reference | null) => {
            setFieldValues((current) => {
                const newFieldValues = {
                    ...current,
                    currency: newValue,
                };

                if (newValue?.id === companyCurrency) {
                    newFieldValues.exchangeRate = null;
                }

                return newFieldValues;
            });

            dispatch(changeQbooksJournalEntryRequest());
        },
        [companyCurrency, dispatch]
    );

    const onExchangeRateChange = useCallback(
        (newValue: number | null) => {
            setFieldValues((current) => ({
                ...current,
                exchangeRate: newValue,
                isManualExchangeRate: newValue !== null,
            }));

            dispatch(changeQbooksJournalEntryRequest());
        },
        [dispatch]
    );

    const onAutoExchangeRateChange = useCallback((params: ExchangeRateChangeParams) => {
        setFieldValues((current) => ({
            ...current,
            autoExchangeRate: params.exchangeRate,
            exchangeRateSource: params.exchangeRateSource,
            exchangeRateDate: params.exchangeRateDate,
        }));
    }, []);

    return {
        fieldValues,
        onFieldChange,
        onCurrencyChange,
        onExchangeRateChange,
        onAutoExchangeRateChange,
        setFieldsValuesBulk,
    };
};

export const useJournalEntryLines = (lineAmountType: domain.LineAmountType, taxCodeList: TaxCodeListItem[]) => {
    const [journalEntryLines, setJournalEntryLinesState] = useState<ExpandedJournalEntryLine[]>(() => [
        createNewJournalEntryLine(),
    ]);

    const dispatch = useDispatch();

    useEffect(() => {
        if (lineAmountType === domain.LineAmountType.NoTax) {
            setJournalEntryLinesState((current) => {
                return current.map((line) => {
                    return {
                        ...line,
                        taxCode: null,
                        taxApplicableOnType: null,
                    };
                });
            });
        }
    }, [lineAmountType]);

    useEffect(() => {
        setJournalEntryLinesState((current) => {
            return current.map((line) => {
                const usedTaxCode = taxCodeList.find((taxCodeItem) => isEqual(taxCodeItem, line.taxCode)) || null;

                if (!usedTaxCode) {
                    const newTaxCode =
                        taxCodeList.find((taxCodeItem) => {
                            if (line.taxCode?.isGroupTax) {
                                return (
                                    taxCodeItem.taxCodeId === line.taxCode.taxCodeId &&
                                    taxCodeItem.isGroupTax &&
                                    taxCodeItem.taxApplicableOnType === line.taxCode.taxApplicableOnType
                                );
                            } else {
                                return (
                                    taxCodeItem.taxCodeId === line.taxCode?.taxCodeId &&
                                    taxCodeItem.taxApplicableOnType === line.taxCode?.taxApplicableOnType &&
                                    taxCodeItem.taxRateId === line.taxCode?.taxRateId
                                );
                            }
                        }) || null;

                    if (newTaxCode) {
                        return {
                            ...line,
                            taxCode: newTaxCode,
                        };
                    }
                }

                return line;
            });
        });
    }, [taxCodeList]);

    const setJournalEntryLines = useCallback((data: QBooksJournalEntryLineWithTaxCode[]) => {
        if (data.length === 0) {
            setJournalEntryLinesState([createNewJournalEntryLine()]);
        } else {
            setJournalEntryLinesState(data.map((line) => expandJournalEntryLine(line)));
        }
    }, []);

    const onJournalEntryLineAdd = useCallback(() => {
        setJournalEntryLinesState((current) => [...current, createNewJournalEntryLine()]);

        dispatch(changeQbooksJournalEntryRequest());
    }, [dispatch]);

    const onJournalEntryLinesReorder = useCallback(
        (oldIndex: number, newIndex: number) => {
            setJournalEntryLinesState((current) => {
                return arrayHelpers.arrayMove(current, oldIndex, newIndex);
            });

            dispatch(changeQbooksJournalEntryRequest());
        },
        [dispatch]
    );

    const onJournalEntryLineRemove = useCallback(
        (id: string) => {
            setJournalEntryLinesState((current) => {
                const lines = current.filter((item) => item.id !== id);

                if (lines.length === 0) {
                    return [createNewJournalEntryLine()];
                }

                return lines;
            });

            dispatch(changeQbooksJournalEntryRequest());
        },
        [dispatch]
    );

    const onJournalEntryLineClone = useCallback(
        (id: string) => {
            setJournalEntryLinesState((current) => {
                const targetLine = current.find((line) => line.id === id);

                if (targetLine) {
                    return [
                        ...current,
                        {
                            ...targetLine,
                            id: uniqueId('qbooksJournalEntryLine-'),
                        },
                    ];
                }

                return current;
            });

            dispatch(changeQbooksJournalEntryRequest());
        },
        [dispatch]
    );

    const onJournalEntryLineFieldUpdate = useCallback(
        <K extends keyof ExpandedJournalEntryLine>(id: string, key: K, value: ExpandedJournalEntryLine[K]) => {
            setJournalEntryLinesState((current) =>
                current.map((line) => {
                    if (line.id === id) {
                        const newLine = {
                            ...line,
                            [key]: value,
                        };

                        if (key === 'payeeType' && line.payeeType !== newLine.payeeType) {
                            newLine.payee = null;
                        }

                        newLine.isEmpty = checkJournalEntryLineIsEmpty(newLine);

                        return newLine;
                    }

                    return line;
                })
            );

            dispatch(changeQbooksJournalEntryRequest());
        },
        [dispatch]
    );

    const updateJournalEntryLine = useCallback(
        (id: string, data: Partial<ExpandedJournalEntryLine>) => {
            setJournalEntryLinesState((current) =>
                current.map((line) => {
                    if (line.id === id) {
                        const updatedLine = {
                            ...line,
                            ...data,
                        };

                        updatedLine.isEmpty = checkJournalEntryLineIsEmpty(updatedLine);

                        return updatedLine;
                    }

                    return line;
                })
            );

            dispatch(changeQbooksJournalEntryRequest());
        },
        [dispatch]
    );

    return {
        journalEntryLines,
        setJournalEntryLines,
        onJournalEntryLineAdd,
        onJournalEntryLinesReorder,
        onJournalEntryLineRemove,
        onJournalEntryLineClone,
        onJournalEntryLineFieldUpdate,
        updateJournalEntryLine,
    };
};

export const usePayees = (requestId: string, companyId: string) => {
    const request = useSelector((state) => selectors.request.getRequestById(state, requestId));
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const ruleType = getRuleTypeByEditMode(editMode);

    return useQuery(
        [QueryKeys.QBOOKS_JOURNAL_PAYEE_DATA, companyId],
        () => {
            return Promise.all([
                api.integration.getQBooksPayeeVendors({
                    requestId,
                    companyId,
                    ruleType,
                }),
                api.integration.getQBooksPayeeCustomers({
                    requestId,
                    companyId,
                    ruleType,
                }),
                api.integration.getQBooksPayeeEmployees({
                    requestId,
                    companyId,
                    ruleType,
                }),
            ]);
        },
        {
            select: (data) => {
                const [vendorsData, customersData, employeesData] = data;

                return {
                    vendors: (vendorsData.Contacts || []).map((item) => ({
                        id: item.Id,
                        text: `${item.Name} - ${item.Currency.ISOCode}`,
                    })),
                    customers: (customersData.Contacts || []).map((item) => ({
                        id: item.Id,
                        text: `${item.Name} - ${item.Currency.ISOCode}`,
                    })),
                    employees: (employeesData.Employees || []).map(du.parseServerReference),
                };
            },
        }
    );
};

export const useTaxCodeList = (date: string) => {
    const qbooksContext = useSelector(getQBooksContext);

    const { taxCodes, taxRates } = qbooksContext;

    const unavailableTaxRateIds = useMemo(() => {
        return getUnavailableTaxRateIds(taxRates, date);
    }, [date, taxRates]);

    const taxCodeList = useMemo(() => {
        const list: TaxCodeListItem[] = [];

        taxCodes.forEach((taxCode) => {
            const isAvailableTaxRate = taxCode.taxRateList.some(
                (taxRate) => !unavailableTaxRateIds.includes(taxRate.taxRateId)
            );

            if (isAvailableTaxRate) {
                if (taxCode.taxRateList.length > 1) {
                    list.push({
                        id: `${taxCode.id}-purchases`,
                        text: `${taxCode.text} (Purchases)`,
                        taxCodeId: taxCode.id,
                        taxCodeName: taxCode.text,
                        isGroupTax: true,
                        taxRates: taxCode.taxRateList,
                        taxApplicableOnType: domain.TaxApplicableOnType.Purchases,
                    });
                } else {
                    taxCode.taxRateList.forEach((taxRate) => {
                        const taxRateReference = taxRates.find((rate) => rate.id === taxRate.taxRateId);

                        if (taxRateReference) {
                            const effectiveTaxRate = findActiveEffectiveRate(taxRateReference.effectiveTaxRate, date);

                            const rate = effectiveTaxRate?.rate ?? 0;

                            list.push({
                                id: `${taxCode.id}-purchases`,
                                text: `${taxCode.text} (Purchases)`,
                                taxCodeId: taxCode.id,
                                taxCodeName: taxCode.text,
                                rate,
                                isGroupTax: false,
                                taxRateId: taxRateReference.id,
                                taxApplicableOnType: domain.TaxApplicableOnType.Purchases,
                                taxApplicable: taxRate.taxApplicable,
                            });
                        }
                    });
                }
            }

            const isAvailableSalesTaxRate = taxCode.salesTaxRateList.some(
                (taxRate) => !unavailableTaxRateIds.includes(taxRate.taxRateId)
            );

            if (isAvailableSalesTaxRate) {
                if (taxCode.salesTaxRateList.length > 1) {
                    list.push({
                        id: `${taxCode.id}-sales`,
                        text: `${taxCode.text} (Sales)`,
                        taxCodeId: taxCode.id,
                        taxCodeName: taxCode.text,
                        isGroupTax: true,
                        taxRates: taxCode.salesTaxRateList,
                        taxApplicableOnType: domain.TaxApplicableOnType.Sales,
                    });
                } else {
                    taxCode.salesTaxRateList.forEach((taxRate) => {
                        const taxRateReference = taxRates.find((rate) => rate.id === taxRate.taxRateId);

                        if (taxRateReference) {
                            const effectiveTaxRate = findActiveEffectiveRate(taxRateReference.effectiveTaxRate, date);

                            const rate = effectiveTaxRate?.rate ?? 0;

                            list.push({
                                id: `${taxCode.id}-sales`,
                                text: `${taxCode.text} (Sales)`,
                                taxCodeId: taxCode.id,
                                taxCodeName: taxCode.text,
                                rate,
                                isGroupTax: false,
                                taxRateId: taxRateReference.id,
                                taxApplicableOnType: domain.TaxApplicableOnType.Sales,
                                taxApplicable: taxRate.taxApplicable,
                            });
                        }
                    });
                }
            }
        });

        return list;
    }, [taxCodes, unavailableTaxRateIds, taxRates, date]);

    return taxCodeList;
};
